import React, { useEffect } from "react";

import { Route, Routes, Navigate } from "react-router-dom";

import DunningBanner from "Containers/Banner/Dunning";
import useDecodedParams from "Hooks/useDecodedParams";
import { useLocalForage } from "Hooks/useLocalForage";
import { getFeatureFlag } from "Reducers/featureFlags";
import {
  selectFeatureOrganizationsEnabled,
  selectFeatureBillingEnabled,
  selectFeatureCreateProjectEnabled,
  selectSubscribeFlowEnabled,
  selectEnableOrganizationFirstOnboarding
} from "Reducers/featureFlags/featureFlags.selectors";
import {
  organizationByDescriptionIdSelector,
  clearOrginaizationError,
  organizationLoadingSelector
} from "Reducers/organization";
import { getOrganizationProfile } from "Reducers/organization/profile";
import { CommandMenu } from "src/root/pages/components/CommandMenu/CommandMenu";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import OrganizationError from "../common/containers/OrganizationError";

import BillingRoutes from "./billing/Routes";
import OrganizationFirstOnboardingRoutes from "./organizationFirstOnboarding/Routes";
import ProjectsRoutes from "./organizationProjects/Routes";
import ProjectRoutes from "./project/Routes";
import SettingsRoutes from "./settings/Routes";
import SetupRoutes from "./setup/Routes";
import Subscribe from "./subscribe/Routes";

const OrganizationRoutes = () => {
  const dispatch = useAppDispatch();
  const { organizationId } = useDecodedParams();
  const { setValue: setLastOrganizationName } = useLocalForage(
    "last-organization-name"
  );
  const organizationFirstOnboarding = useAppSelector(
    selectEnableOrganizationFirstOnboarding
  );
  const organizationEnabled = useAppSelector(selectFeatureOrganizationsEnabled);
  const billingEnabled = useAppSelector(selectFeatureBillingEnabled);
  const createProjectEnabled = useAppSelector(
    selectFeatureCreateProjectEnabled
  );
  const orgloading = useAppSelector(organizationLoadingSelector);
  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );

  useEffect(() => {
    if (organization) {
      dispatch(getOrganizationProfile({ organizationId: organization.name }));
      setLastOrganizationName(organization.name);
    }
  }, [organization]);

  useEffect(() => dispatch(clearOrginaizationError()), []);
  const isSubscribeFlowEnabled = useAppSelector(selectSubscribeFlowEnabled);

  if (orgloading) return null;

  return (
    <OrganizationError>
      <DunningBanner organizationName={organizationId} />
      <Routes>
        <Route
          path="-/subscribe/*"
          element={
            <>
              <CommandMenu />
              {isSubscribeFlowEnabled ? <Subscribe /> : <Navigate to="/" />}
            </>
          }
        />
        <Route
          path="-/settings/*"
          element={
            <>
              <CommandMenu />
              {organizationEnabled ? <SettingsRoutes /> : <Navigate to="/" />}
            </>
          }
        />

        <Route
          path="-/billing/*"
          element={
            <>
              <CommandMenu />
              {organizationEnabled && billingEnabled ? (
                <BillingRoutes />
              ) : (
                <Navigate to={organizationId ? `/${organizationId}` : "/"} />
              )}
            </>
          }
        />
        <Route
          path="create-project/*"
          element={
            <>
              <CommandMenu />
              {createProjectEnabled ? (
                organizationFirstOnboarding ? (
                  <OrganizationFirstOnboardingRoutes />
                ) : (
                  <SetupRoutes />
                )
              ) : (
                <Navigate to="/" />
              )}
            </>
          }
        />

        <Route
          path=":projectId/*"
          element={
            <>
              <CommandMenu />
              <ProjectRoutes />
            </>
          }
        />
        <Route
          path=""
          element={
            <>
              <CommandMenu />
              {organizationEnabled ? (
                <ProjectsRoutes organizationName={organizationId} />
              ) : (
                <Navigate to="/" />
              )}
            </>
          }
        />
      </Routes>
    </OrganizationError>
  );
};

export const Component = OrganizationRoutes;
export default OrganizationRoutes;
